import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation } from '@tanstack/react-query';

import { routeService } from '../../../../application/RouteService';
import { Route, UpdateRouteProps } from '../../../../domain/route/Route';
import { useErrorMiddleware } from '../../../../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../../hooks/useShowSnackbarMessage';
import { RouteActionType } from '../context/actions';
import { useRouteContext } from '../context/context';

interface UseUpdateRouteProps extends UpdateRouteProps {
  route: Route;
}

type UseUpdateRouteResponseProps = ServiceResponse<(updateProps: UseUpdateRouteProps) => void, void>;
export const useUpdateRoute = (): UseUpdateRouteResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { routeDispatch } = useRouteContext();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation<Route, unknown, UseUpdateRouteProps>(
    ({ route, ...updateProps }: UseUpdateRouteProps): Promise<Route> => {
      // Store the previous state of the route before updating it
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const previousRoute = { ...route };

      // Update the route object as required
      route.update(updateProps);

      // Return the updated route for the API call
      return routeService
        .updateRoute({ route })
        .then((updatedRoute) => {
          // If update is successful, return the updated route
          return updatedRoute;
        })
        .catch((error) => {
          // If the API call fails, we throw an error to be caught in the onError handler
          throw error;
        });
    },
    {
      onSuccess: (route) => {
        routeDispatch({ type: RouteActionType.FETCH_ROUTE_SUCCESS, payload: route });
      },
      onError: (err: any, _variables: any, context: any) => {
        console.info(`Update Route Error: `, err?.response);

        // Revert to the previous state if mutation fails
        if (err?.response?.status !== 401) {
          routeDispatch({ type: RouteActionType.UPDATE_ROUTE_FAIL, payload: context.previousRoute });
        }

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
      onMutate: ({ route }) => {
        // Store the previous route state before the mutation starts
        return { previousRoute: { ...route } };
      },
    }
  );

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: (updateProps: UseUpdateRouteProps) => mutate(updateProps),
  };
};
